import { ImageMarkdownNode, TokenType, MarkdownNode } from 'marked-ast'
import { decodeHtmlEntities } from 'helpers/formatHelpers'
import { renderTokensToPlainText, extractVideoIdFromUrl, MaxMediaSize, extractMediaSizeFromUrl, getMediaMaxSize } from 'helpers/markdownHelpers'
import { InsightMedia } from 'components/features/insights/Insight/Media'
import { captureWarning } from 'helpers/errorHelpers'
import { Insight } from 'interfaces/insights'
import VideoPlayer from 'components/features/pfm/VideoPlayer/VideoPlayer'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { VideoInPopupPlayer } from 'components/features/pfm/VideoInPopupPlayer/VideoInPopupPlayer'
import { makeImage } from 'helpers/mediaHelpers'

const MAX_INSIGHT_MEDIA_WIDTH = 640

function extractMaxMediaSizeFromUrl (url: string): MaxMediaSize | null {
  const size = extractMediaSizeFromUrl(url)
  return size ? getMediaMaxSize(size, MAX_INSIGHT_MEDIA_WIDTH) : null
}

export function sendWarning (insight: Insight | SocialInnerInsight, entityType: TokenType) {
  const { id, text } = insight
  captureWarning(`Invalid insight ${ entityType }!`, { id, property: 'url', text })
}

function fetchImageToken (token: ImageMarkdownNode) {
  const src = decodeHtmlEntities(token.href)
  return {
    src,
    alt: renderTokensToPlainText(token.text),
    maxMediaSize: extractMaxMediaSizeFromUrl(src),
  }
}

export type TokenRendererErrorHandler = {
  (tokenType: TokenType, token: MarkdownNode): void
}

export const createImageTokenRenderer = (onError: TokenRendererErrorHandler) => (token: ImageMarkdownNode, key: string) => {
  const { href } = token
  if (!href) {
    onError('image', token)
    return null
  }

  const {
    src,
    alt,
    maxMediaSize,
  } = fetchImageToken(token)

  const mediaProps = { maxMediaSize, src, key }
  const imageProps = {
    alt,
    onError: () => onError('image', token),
  }

  return (
    <InsightMedia { ...mediaProps } hasGradient>
      <InsightMedia.Image { ...mediaProps } { ...imageProps } />
    </InsightMedia >
  )
}

const parseVideoUrl = (src: string) => {
  const videoId = extractVideoIdFromUrl(src)


  const { searchParams } = new URL(src)
  return {
    playerUrl: `https://vimeo.com/${ videoId }`,
    thumbnailUrl: searchParams.get('thumbnail_url') || null,
    width: searchParams.get('width'),
    height: searchParams.get('height'),
  }

}

export const createVideoTokenRenderer = (onError: TokenRendererErrorHandler) => (token: ImageMarkdownNode, key: string) => {
  const src = decodeHtmlEntities(token.href)
  const {
    playerUrl,
    thumbnailUrl,
    width,
    height,
  } = parseVideoUrl(src)

  let poster = null
  if (thumbnailUrl && width && height) {
    poster = makeImage(thumbnailUrl, +(width || 0), +(height || 0))
  }

  return (
    <VideoPlayer
      key={ key }
      poster={ poster }
      videoUrl={ playerUrl }
      onError={ () => onError('video', token) }
    />
  )
}


export const createVideoInPopupTokenRenderer = (onError: TokenRendererErrorHandler) => (token: ImageMarkdownNode, key: string) => {
  const src = decodeHtmlEntities(token.href)
  const {
    playerUrl,
    thumbnailUrl,
    width,
    height,
  } = parseVideoUrl(src)

  let poster = null
  if (thumbnailUrl && width && height) {
    poster = makeImage(thumbnailUrl, +(width || 0), +(height || 0))
  }

  return (
    <VideoInPopupPlayer
      key={ key }
      poster={ poster }
      videoUrl={ playerUrl }
      onError={ () => onError('video', token) }
    />
  )
}

